import { useEffect, useRef } from "react";
import useParkingSession from "../model/useParkingSession";
import { ParkingSessionService } from "../services/parkingSessionService";

/**
 * Hook pre automatické čistenie starých parking sessions
 *
 * Používanie:
 * - Pridaj do hlavnej komponenty aplikácie (napr. Home, Map)
 * - Cleanup sa spustí pri prvom načítaní
 * - Voliteľne môžeš nastaviť periodicke čistenie
 *
 * @param options.runOnMount - Spustiť cleanup pri mount (default: true)
 * @param options.intervalMinutes - Periodicke čistenie každých X minút (default: null = vypnuté)
 */
export function useParkingSessionCleanup(options?: {
    runOnMount?: boolean;
    intervalMinutes?: number | null;
}) {
    const { runOnMount = true, intervalMinutes = null } = options || {};
    const { getAll, deleteByIndex } = useParkingSession();
    const hasRunInitialCleanup = useRef(false);

    useEffect(() => {
        const runCleanup = async () => {
            try {
                const deletedCount = await ParkingSessionService.cleanupOldSessions(
                    getAll,
                    deleteByIndex
                );
                if (deletedCount > 0) {
                    console.log(`Parking session cleanup: removed ${deletedCount} old sessions`);
                }
            } catch (error) {
                console.error("Failed to cleanup parking sessions:", error);
            }
        };

        // Spustit pri mount
        if (runOnMount && !hasRunInitialCleanup.current) {
            hasRunInitialCleanup.current = true;
            runCleanup();
        }

        // Periodické čistenie
        if (intervalMinutes && intervalMinutes > 0) {
            const intervalMs = intervalMinutes * 60 * 1000;
            const intervalId = setInterval(runCleanup, intervalMs);

            return () => {
                clearInterval(intervalId);
            };
        }
    }, [runOnMount, intervalMinutes, getAll, deleteByIndex]);
}
